import React, { useMemo, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ConsentDetails, ConsentType, ConsentState } from '../../constants/uaeStandards';
import { ConsentCard } from '../ui/ConsentCard';
import { SearchFilterBar, FilterOptions } from '../ui/SearchFilterBar';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
import { PlusIcon, HistoryIcon, ListIcon } from 'lucide-react';
export interface ActiveConsent {
  id: string;
  tppName: string;
  tppLogo: string;
  bankName: string;
  bankLogo: string;
  connectedDate: string;
  expiryDate: string;
  durationMonths: number;
  dataClusters: string[];
  selectedAccounts: Array<{
    id: string;
    name: string;
    accountNumber: string;
  }>;
  status: string;
}
export interface ConsentManagementScreenProps {
  activeConsents: ConsentDetails[];
  historyConsents: ConsentDetails[];
  onViewDetails: (id: string) => void;
  onUpdateConsent: (id: string) => void;
  onRevokeConsent: (id: string) => void;
  onAddNewConnection: () => void;
}
type TabType = 'active' | 'history';
type ConsentTypeTab = 'all' | 'DataSharing' | 'ServiceInitiation';
export function ConsentManagementScreen({
  activeConsents,
  historyConsents,
  onViewDetails,
  onUpdateConsent,
  onRevokeConsent,
  onAddNewConnection
}: ConsentManagementScreenProps) {
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [consentTypeTab, setConsentTypeTab] = useState<ConsentTypeTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({});
  // Get unique bank names for filter
  const availableBanks = useMemo(() => {
    const banks = new Set<string>();
    activeConsents.forEach(c => banks.add(c.bankName));
    historyConsents.forEach(c => banks.add(c.bankName));
    return Array.from(banks).sort();
  }, [activeConsents, historyConsents]);
  // Filter and search logic
  const filteredConsents = useMemo(() => {
    const consents = activeTab === 'active' ? activeConsents : historyConsents;
    return consents.filter(consent => {
      // Search filter
      if (searchQuery && !consent.bankName.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Consent type filter
      if (consentTypeTab !== 'all' && consent.type !== consentTypeTab) {
        return false;
      }
      // Bank name filter
      if (filters.bankName && consent.bankName !== filters.bankName) {
        return false;
      }
      // Account number filter
      if (filters.accountNumber && !consent.accountLastFour.includes(filters.accountNumber)) {
        return false;
      }
      // Consent type filter (from dropdown)
      if (filters.consentType && consent.type !== filters.consentType) {
        return false;
      }
      // Consent state filter
      if (filters.consentState && consent.state !== filters.consentState) {
        return false;
      }
      return true;
    });
  }, [activeTab, consentTypeTab, searchQuery, filters, activeConsents, historyConsents]);
  // Group by consent type for display
  const dataShareConsents = filteredConsents.filter(c => c.type === 'DataSharing');
  const serviceInitiationConsents = filteredConsents.filter(c => c.type === 'ServiceInitiation');
  const renderConsentList = (consents: ConsentDetails[], emptyMessage: string) => {
    if (consents.length === 0) {
      return <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center py-12" data-id="element-607">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{
          backgroundColor: OPENFINANCE_THEME.colors.primary50
        }} data-id="element-608">
            <ListIcon className="w-8 h-8" style={{
            color: OPENFINANCE_THEME.colors.primary400
          }} data-id="element-609" />
          </div>
          <div className="text-base font-medium mb-2" style={{
          color: OPENFINANCE_THEME.colors.text,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-610">
            {emptyMessage}
          </div>
          <div className="text-sm" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-611">
            {activeTab === 'active' ? 'Connect your bank accounts to get started' : 'Your consent history will appear here'}
          </div>
        </motion.div>;
    }
    return <motion.div initial="hidden" animate="show" variants={{
      hidden: {
        opacity: 0
      },
      show: {
        opacity: 1,
        transition: {
          staggerChildren: 0.05
        }
      }
    }} className="space-y-3" data-id="element-612">
        {consents.map(consent => <motion.div key={consent.id} variants={{
        hidden: {
          opacity: 0,
          y: 20
        },
        show: {
          opacity: 1,
          y: 0
        }
      }} data-id="element-613">
            <ConsentCard consent={consent} onClick={() => onViewDetails(consent.id)} context="TPP" data-id="element-614" />
          </motion.div>)}
      </motion.div>;
  };
  return <div className="space-y-4 md:space-y-6" data-id="element-615">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4" data-id="element-616">
        <div data-id="element-617">
          <h1 className="text-xl md:text-2xl font-bold mb-1" style={{
          fontFamily: OPENFINANCE_THEME.fonts.display,
          color: OPENFINANCE_THEME.colors.primary700
        }} data-id="element-618">
            Open Finance Connections
          </h1>
          <p className="text-sm" style={{
          color: OPENFINANCE_THEME.colors.textLight,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-619">
            Manage your connected bank accounts and services
          </p>
        </div>

        <button onClick={onAddNewConnection} className="w-full sm:w-auto px-4 py-2.5 font-semibold text-white transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg active:scale-95" style={{
        background: OPENFINANCE_THEME.gradients.accent,
        borderRadius: OPENFINANCE_THEME.radius.md,
        fontFamily: OPENFINANCE_THEME.fonts.body
      }} data-id="element-620">
          <PlusIcon className="w-5 h-5" data-id="element-621" />
          <span data-id="element-622">Add Connection</span>
        </button>
      </div>

      {/* Main Tabs: Active vs History - Responsive */}
      <div className="flex gap-1 sm:gap-2 border-b-2 overflow-x-auto" style={{
      borderColor: OPENFINANCE_THEME.colors.borderLight
    }} data-id="element-623">
        {[{
        id: 'active' as TabType,
        label: 'Active',
        count: activeConsents.length
      }, {
        id: 'history' as TabType,
        label: 'History',
        count: historyConsents.length
      }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="px-4 sm:px-6 py-3 font-semibold text-xs sm:text-sm transition-all relative whitespace-nowrap" style={{
        color: activeTab === tab.id ? OPENFINANCE_THEME.colors.primary600 : OPENFINANCE_THEME.colors.textMuted,
        fontFamily: OPENFINANCE_THEME.fonts.body
      }} data-id="element-624">
            <div className="flex items-center gap-2" data-id="element-625">
              <span data-id="element-626">{tab.label}</span>
              <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{
            backgroundColor: activeTab === tab.id ? OPENFINANCE_THEME.colors.primary100 : OPENFINANCE_THEME.colors.background,
            color: activeTab === tab.id ? OPENFINANCE_THEME.colors.primary600 : OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-627">
                {tab.count}
              </span>
            </div>
            {activeTab === tab.id && <motion.div layoutId="activeTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5" style={{
          backgroundColor: OPENFINANCE_THEME.colors.primary500
        }} transition={{
          type: 'spring',
          stiffness: 500,
          damping: 30
        }} data-id="element-628" />}
          </button>)}
      </div>

      {/* Search and Filter */}
      <SearchFilterBar onSearch={setSearchQuery} onFilterChange={setFilters} availableBanks={availableBanks} context="TPP" data-id="element-629" />

      {/* Consent Type Tabs - Responsive with wrapping */}
      <div className="flex flex-wrap gap-2" data-id="element-630">
        {[{
        id: 'all' as ConsentTypeTab,
        label: 'All Consents',
        shortLabel: 'All',
        count: filteredConsents.length
      }, {
        id: 'DataSharing' as ConsentTypeTab,
        label: 'Data Sharing',
        shortLabel: 'Data',
        count: dataShareConsents.length
      }, {
        id: 'ServiceInitiation' as ConsentTypeTab,
        label: 'Service Initiation',
        shortLabel: 'Service',
        count: serviceInitiationConsents.length
      }].map(tab => <button key={tab.id} onClick={() => setConsentTypeTab(tab.id)} className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-all" style={{
        backgroundColor: consentTypeTab === tab.id ? OPENFINANCE_THEME.colors.primary500 : OPENFINANCE_THEME.colors.cardBg,
        color: consentTypeTab === tab.id ? 'white' : OPENFINANCE_THEME.colors.text,
        borderRadius: OPENFINANCE_THEME.radius.md,
        border: `2px solid ${consentTypeTab === tab.id ? OPENFINANCE_THEME.colors.primary500 : OPENFINANCE_THEME.colors.border}`
      }} data-id="element-631">
            <span className="hidden sm:inline" data-id="element-632">{tab.label}</span>
            <span className="sm:hidden" data-id="element-633">{tab.shortLabel}</span> ({tab.count})
          </button>)}
      </div>

      {/* Consent List */}
      <AnimatePresence mode="wait" data-id="element-634">
        <motion.div key={`${activeTab}-${consentTypeTab}`} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.2
      }} data-id="element-635">
          {consentTypeTab === 'all' ? <>
              {dataShareConsents.length > 0 && <div className="mb-6" data-id="element-636">
                  <div className="text-xs sm:text-sm font-semibold mb-3 uppercase tracking-wide" style={{
              color: OPENFINANCE_THEME.colors.textLight,
              fontFamily: OPENFINANCE_THEME.fonts.body
            }} data-id="element-637">
                    Data Sharing ({dataShareConsents.length})
                  </div>
                  {renderConsentList(dataShareConsents, 'No data sharing consents')}
                </div>}

              {serviceInitiationConsents.length > 0 && <div data-id="element-638">
                  <div className="text-xs sm:text-sm font-semibold mb-3 uppercase tracking-wide" style={{
              color: OPENFINANCE_THEME.colors.textLight,
              fontFamily: OPENFINANCE_THEME.fonts.body
            }} data-id="element-639">
                    Service Initiation ({serviceInitiationConsents.length})
                  </div>
                  {renderConsentList(serviceInitiationConsents, 'No service initiation consents')}
                </div>}

              {filteredConsents.length === 0 && renderConsentList([], 'No consents found')}
            </> : consentTypeTab === 'DataSharing' ? renderConsentList(dataShareConsents, 'No data sharing consents') : renderConsentList(serviceInitiationConsents, 'No service initiation consents')}
        </motion.div>
      </AnimatePresence>
    </div>;
}